<script setup>
import { ref } from 'vue';
import api from '@/services/axios';
import { useField } from 'vee-validate';
import InputError from '@/components/general/InputError.vue';
import InputLabel from '@/components/general/InputLabel.vue';
import PrimaryButton from '@/components/general/PrimaryButton.vue';
import TextInput from '@/components/general/TextInput.vue';
import { useFormValidation } from '@/composables/useFormValidation';
import { updatePasswordSchema } from '@/formValidationschemas';
import { useToast } from 'primevue/usetoast';

const toast = useToast();
const currentPasswordInput = ref(null);
const passwordInput = ref(null);
const processing = ref(false);
const recentlySuccessful = ref(false);
const serverErrors = ref({});

// Initialize form with vee-validate
const initialValues = {
    current_password: '',
    password: '',
    password_confirmation: ''
};

const { handleSubmit, errors, resetForm } = useFormValidation(
    updatePasswordSchema,
    initialValues
);

// Setup form fields with useField
const { value: current_password } = useField('current_password');
const { value: password } = useField('password');
const { value: password_confirmation } = useField('password_confirmation');

const updatePassword = handleSubmit(async (values) => {
    processing.value = true;
    serverErrors.value = {};

    try {
        await api.put('/api/user/password', {
            current_password: values.current_password,
            password: values.password,
            password_confirmation: values.password_confirmation
        });

        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Password updated successfully',
            life: 3000
        });

        // Reset form after successful submission
        resetForm();

        recentlySuccessful.value = true;
        setTimeout(() => {
            recentlySuccessful.value = false;
        }, 2000);
    } catch (error) {
        if (error.response && error.response.data && error.response.data.errors) {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update password',
                life: 3000
            });
            // Store backend validation errors
            serverErrors.value = error.response.data.errors;
        } else {
            // Generic error handling
            console.error('Failed to update password:', error);
        }
    } finally {
        processing.value = false;
    }
});
</script>

<template>
    <section>
        <div>
            <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">Update Password</h2>

            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Ensure your account is using a long, random password to stay secure.
            </p>
        </div>

        <form @submit.prevent="updatePassword" class="mt-6 space-y-6">
            <div>
                <InputLabel for="current_password" value="Current Password" />

                <TextInput
                    id="current_password"
                    ref="currentPasswordInput"
                    v-model="current_password"
                    type="password"
                    class="block mt-1 w-full"
                    autocomplete="current-password"
                />

                <InputError :message="errors.current_password || (serverErrors.current_password ? serverErrors.current_password[0] : '')" class="mt-2" />
            </div>

            <div>
                <InputLabel for="password" value="New Password" />

                <TextInput
                    id="password"
                    ref="passwordInput"
                    v-model="password"
                    type="password"
                    class="block mt-1 w-full"
                    autocomplete="new-password"
                />

                <InputError :message="errors.password || (serverErrors.password ? serverErrors.password[0] : '')" class="mt-2" />
            </div>

            <div>
                <InputLabel for="password_confirmation" value="Confirm Password" />

                <TextInput
                    id="password_confirmation"
                    v-model="password_confirmation"
                    type="password"
                    class="block mt-1 w-full"
                    autocomplete="new-password"
                />

                <InputError :message="errors.password_confirmation || (serverErrors.password_confirmation ? serverErrors.password_confirmation[0] : '')" class="mt-2" />
            </div>

            <div class="flex gap-4 items-center">
                <PrimaryButton :disabled="processing">Save</PrimaryButton>

                <Transition
                    enter-active-class="transition ease-in-out"
                    enter-from-class="opacity-0"
                    leave-active-class="transition ease-in-out"
                    leave-to-class="opacity-0"
                >
                    <p v-if="recentlySuccessful" class="text-sm text-gray-600">Saved.</p>
                </Transition>
            </div>
        </form>
    </section>
</template>
